import { isEmail as validateEmail } from '@side/pantry';
import get from 'lodash/get';
import { useCallback, useEffect, useMemo } from 'react';
import { type FieldValues, useFormContext } from 'react-hook-form';
import { useCoverSheetCardContext } from 'src/views/Coversheet/contexts/CoverSheetCardContext';
import { useCoverSheetContext } from 'src/views/Coversheet/contexts/CoverSheetContext';
import { useCoverSheetSectionContext } from 'src/views/Coversheet/contexts/CoverSheetSectionContext';
import {
  doesFieldValidationApply,
  getYearBuiltMessage,
  isValidPhone,
  isValidYearBuilt,
} from 'src/views/Coversheet/utils/fieldValidation';
import * as yup from 'yup';
import { isNotNillOrEmpty } from '@/common/utils/string';
import type { Card, Field } from '../configurations/types';
import {
  useAddMissingField,
  useRemoveMissingField,
} from '../state/coverSheetValidation';
import { isFieldVisible } from './useIsFieldVisible';

function buildValidationSchema(
  validations: Field['validation'],
  index: number,
) {
  let validationSchema = yup.string().nullable();

  validations?.forEach((validation) => {
    if (!doesFieldValidationApply(validation, index)) return;

    switch (validation.type) {
      case 'required':
        validationSchema = validationSchema.required(
          validation?.message ?? 'Required',
        );
        break;
      case 'min':
        if (validation.value)
          validationSchema = validationSchema.min(
            Number(validation.value),
            validation?.message ??
              `Must be at least ${validation.value} character(s)`,
          );
        break;
      case 'max':
        if (validation.value)
          validationSchema = validationSchema.max(
            Number(validation.value),
            validation?.message ?? `Max ${validation.value} character(s)`,
          );
        break;
      case 'email':
        validationSchema = validationSchema.test(
          'emailValidation',
          validation?.message ?? 'Invalid email',
          (value) => !value || validateEmail(value),
        );
        break;
      case 'pattern':
        if (validation.value)
          validationSchema = validationSchema.matches(
            new RegExp(validation.value),
            validation?.message ?? 'Invalid',
          );
        break;
      case 'phone':
      case 'phoneWithExt':
        validationSchema = validationSchema.test(
          'phoneValidation',
          'Invalid phone number',
          isValidPhone,
        );
        break;
      case 'yearBuilt':
        validationSchema = validationSchema.test(
          'yearBuiltValidation',
          validation?.message ?? getYearBuiltMessage(),
          isValidYearBuilt,
        );
        break;
      default:
        break;
    }
  });

  return validationSchema;
}

interface ValidateFieldParams {
  index: number;
  validations: Field['validation'];
  value: unknown;
}

function isFieldValid({
  index,
  validations,
  value,
}: ValidateFieldParams): boolean {
  const schema = buildValidationSchema(validations, index);

  try {
    const result = schema.validateSync(value);

    // we need to convert value to string because validateSync returns it stringified.
    if (result === `${value}`) return true;

    return false;
  } catch (_) {
    return false;
  }
}

/**
 * Get invalid fields for a card
 * @param card - The card to check for invalid fields, as indexed by indexifyValues
 * @param data - Form data
 * @param index - The index of the card
 * @returns Invalid fields for the card
 */
export const getInvalidCardFields = (
  card: Card,
  data: FieldValues,
  index: number,
) => {
  return card.fields.filter((field) => {
    const isPersistedField = Boolean(field.entity);

    if (!isPersistedField) {
      return false;
    }

    const isVisible = isFieldVisible({
      card,
      index,
      showWhen: field.showWhen,
      fieldValues: data,
      visibleForIndex: field.visibleForIndex,
    });

    if (!isVisible) {
      return false;
    }

    const value = get(data, field.name, '') as unknown;
    const isValid = isFieldValid({
      index,
      validations: field.validation,
      value,
    });

    return !isValid;
  });
};

export function useFieldValidator(field: Field) {
  const { name, label, validation: validations, showWhen } = field;

  const { interactionId } = useCoverSheetContext();
  const { getValues, trigger } = useFormContext();
  const { section } = useCoverSheetSectionContext();
  const { index, card } = useCoverSheetCardContext();
  const addMissingField = useAddMissingField(interactionId);
  const removeMissingField = useRemoveMissingField(interactionId);

  const schema = useMemo(
    () => buildValidationSchema(validations, index),
    [validations, index],
  );

  const parentField = useMemo(() => {
    if (!card.fields?.length) {
      return null;
    }
    if (showWhen) {
      const { fieldName: parentFieldName } = showWhen;
      return card.fields.find((f) => f.name === parentFieldName) ?? null;
    }
    return (
      card.fields.find(
        (f) => f.name !== name && f.customField?.name === name,
      ) ?? null
    );
  }, [card.fields, name, showWhen]);

  const isRequired = useMemo(
    () =>
      !!validations?.find(
        (validation) =>
          validation.type === 'required' &&
          doesFieldValidationApply(validation, index),
      ),
    [validations, index],
  );

  const validate = useCallback(
    async (value: string) => {
      try {
        await schema.validate(value);
        if (isRequired) {
          removeMissingField(name);
        }
        return true;
      } catch (error) {
        if (isRequired) {
          addMissingField(name, {
            name,
            label: parentField?.label ?? label,
            index,
            section: section.sidebarText,
            cardId: card.id,
            cardTitle: card.innerTitle ?? undefined,
          });
        }
        if (error instanceof yup.ValidationError) return error.message;
        return 'Invalid';
      }
    },
    [
      schema,
      isRequired,
      name,
      label,
      parentField?.label,
      index,
      section.sidebarText,
      card.id,
      card.innerTitle,
      removeMissingField,
      addMissingField,
    ],
  );

  const isEmail = useMemo(
    () =>
      !!validations?.find(
        (validation) =>
          validation.type === 'email' &&
          doesFieldValidationApply(validation, index),
      ),
    [validations, index],
  );

  // Trigger validation on mount if field is required
  useEffect(() => {
    if (!isRequired) return;
    const defaultValue = getValues(name) as string;
    if (isNotNillOrEmpty(defaultValue)) {
      void trigger(name);
    } else {
      void validate(defaultValue);
    }
  }, [name, getValues, isRequired, validate, trigger]);

  // Remove from missing fields state on unmount
  useEffect(() => {
    return () => {
      removeMissingField(name);
    };
  }, [name, removeMissingField]);

  return { validate, isRequired, isEmail };
}
